import React, { useCallback, useState, useRef, useEffect } from 'react';
import { Input, Button } from '@vtblife/uikit';
import Icon from '@vtblife/uikit-icons';
import { useCustomerCreate } from '../../hooks/useCustomerCreate';
import { ICustomer } from '../../types/ICustomer';

export interface IOfferCreateItem {
    onAddCustomer: (args: ICustomer) => void;
}

export const AgentCreateCustomerItem: React.FC<IOfferCreateItem> = ({ onAddCustomer }) => {
    const [ isLoading, setLoading ] = useState(false);
    const { onCreateCustomer } = useCustomerCreate();

    const [ isEditing, setEditing ] = useState(false);

    const handleClickByCreate = useCallback(() => {
        setEditing(true);
    }, []);

    const handleSubmitEditing = useCallback((data: { name: string }) => {
        setLoading(true);
        return onCreateCustomer({
            note: '',
            email: '',
            phone: '',
            name: data.name
        }).then(res => {
            if (res) {
                onAddCustomer(res);

                setEditing(false);
                return res;
            }

            return undefined;
        }).then(() => {
            setLoading(false);
        }).catch(() => {
            setLoading(false);
        });
    }, [ onCreateCustomer, onAddCustomer ]);

    return isEditing ? (
        <OfferCreateItemInput onSubmitEditing={handleSubmitEditing} isLoading={isLoading} />
    ) : (
        <OfferCreateItemButton onClickByCreate={handleClickByCreate} />
    );
};

interface IOfferCreateItemInput {
    onSubmitEditing: (data: {
        name: string;
    }) => Promise<void>;
    isLoading: boolean;
}

export const OfferCreateItemInput: React.FC<IOfferCreateItemInput> = ({ onSubmitEditing, isLoading }) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [ isError, setError ] = useState(true);
    const [ name, setName ] = useState('');

    const handleChangeName = useCallback((value: string) => {
        setName(value);
        setError(value.trim().length < 1);
    }, []);

    const handleResetInput = useCallback(() => {
        setName('');
        setError(true);
        inputRef.current?.focus();
    }, [ inputRef ]);

    const handleSubmit = useCallback((event?: React.FormEvent<HTMLFormElement>) => {
        event?.preventDefault();

        if (! isError) {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            onSubmitEditing({
                name
            }).then(handleResetInput);
        }
    }, [ isError, name, onSubmitEditing, handleResetInput ]);

    useEffect(() => {
        inputRef.current?.focus();
    }, [ inputRef ]);

    return (
        <form name='OfferCreateItemForm' onSubmit={handleSubmit}>
            <Input
                inputRef={inputRef}
                size='m'
                disabled={isLoading}
                value={name}
                error={isError}
                onChange={handleChangeName}
                placeholder='Имя клиента'
                dataTest='new-customer-name-input'
                loading={isLoading}
                onReset={false}
            >
                <Input.Content placement='end' onClick={() => handleSubmit()}>
                    <Icon name='heart-hollow' />
                </Input.Content>
            </Input>
        </form>
    );
};

interface IOfferCreateItemButton {
    onClickByCreate: VoidFunction;
}

const OfferCreateItemButton: React.FC<IOfferCreateItemButton> = ({ onClickByCreate }) => {
    const handleClickByCreate = useCallback(
        (event: React.MouseEvent<Element>) => {
            event.preventDefault();
            event.stopPropagation();
            onClickByCreate();
        },
        [ onClickByCreate ]
    );

    return (
        <Button
            block
            variant='secondary'
            icon='plus-circle'
            onClick={handleClickByCreate}
            size='m'
            data-gtm-zbs={'zbs-submit-save-customer'}
            dataTest='create-new-customer'
        >
            Добавить клиента
        </Button>
    );
};
